// Presentation helpers shared by the Patients list, detail page and dialogs.
// Display only: identity, search, filtering and every request keep the raw
// values from the patient workspace contract.

import type { PatientRecord } from './patients-contracts'

export function patientInitials(name: string) {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0])
      .join('')
      .toUpperCase() || 'P'
  )
}

/** North American numbers read as (516) 555-0123; anything else is shown as stored. */
export function formatPhone(value: string | null) {
  if (!value) return null
  const match = /^\+1([0-9]{3})([0-9]{3})([0-9]{4})$/.exec(value)
  return match ? `(${match[1]}) ${match[2]}-${match[3]}` : value
}

export type EngagementChannel = NonNullable<
  PatientRecord['latestEngagement']
>['latestChannel']

export function channelLabel(channel: EngagementChannel) {
  return channel === 'SMS'
    ? 'Text'
    : channel === 'VOICE'
      ? 'Call'
      : channel === 'WEB_WIDGET'
        ? 'Web chat'
        : 'Message'
}

export type StatusTone = 'success' | 'info' | 'danger' | 'neutral'

/** The Dashboard's appointment status colors: confirmed is success, cancelled
    is danger, completed is neutral, anything still to happen is info. */
export function appointmentStatusTone(status: string): StatusTone {
  const value = status.toLocaleLowerCase('en').replace(/[_-]+/g, ' ')
  if (value.includes('cancel') || value.includes('no show')) return 'danger'
  if (value.includes('unconfirmed')) return 'info'
  if (value.includes('confirm')) return 'success'
  if (value.includes('complete') || value.includes('checked out'))
    return 'neutral'
  return 'info'
}

export function appointmentStatusLabel(status: string) {
  const text = status.replace(/[_-]+/g, ' ').trim()
  return text ? text.charAt(0).toLocaleUpperCase('en') + text.slice(1) : status
}

export function formatDateTime(value: number | null) {
  return value
    ? new Intl.DateTimeFormat(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }).format(value)
    : 'Time unavailable'
}

export function formatShortDateTime(value: number) {
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(value)
}

export function formatShortDate(value: number) {
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
  }).format(value)
}

/** A stored ISO date (YYYY-MM-DD) shown as a calendar date, never shifted by time zone. */
export function formatBirthDate(value: string | null) {
  if (!value) return null
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return value
  const date = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  )
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date)
}

/** Whole years since a stored ISO birth date, or null when it can't be read. */
export function ageInYears(value: string | null, now = new Date()) {
  const match = value ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(value) : null
  if (!match) return null
  const [year, month, day] = [
    Number(match[1]),
    Number(match[2]),
    Number(match[3]),
  ]
  let age = now.getFullYear() - year
  if (
    now.getMonth() + 1 < month ||
    (now.getMonth() + 1 === month && now.getDate() < day)
  )
    age -= 1
  return age >= 0 && age < 150 ? age : null
}

/** A stored ISO 8601 date or timestamp shown as staff read dates; anything
    that isn't one is shown as stored. */
export function formatPracticeTimestamp(value: string | null) {
  if (!value) return null
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return formatBirthDate(value)
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) return value
  const time = Date.parse(value)
  if (Number.isNaN(time)) return value
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(time)
}

/** A language code (en, es, pt-BR) shown by name; other values as stored. */
export function languageName(value: string | null) {
  if (!value) return null
  if (!/^[A-Za-z]{2,3}(-[A-Za-z]{2,4})?$/.test(value)) return value
  try {
    return (
      new Intl.DisplayNames(['en'], { type: 'language' }).of(value) ?? value
    )
  } catch {
    return value
  }
}
